import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IAddressValidationResponse, IApplicationState } from "../";
import { IPost, IGet } from '../authorization';
import {
    IBaseResponse, IRecipientResponse, IPreselectResult, IPreselectStatistics, IRecipient, IPreselectDonor, IPreselectDonorDetail, IListItem, IEpletMismatch, IEmpletAllele,
    IRecipientSearchLog, IRecipientSearchReportLookupData, ICheekSwabRequest, ICompatibilityResult, ISingleEpletMmRisk, ILkdpiInfo,
    IRecipientForList,
    IRecipientForSidebar,
    ITargetItem,
    ITransplantedRecipient,
    IFwtSummary,
    IPostTransplantRejection
} from '@nkr/models/index';
import { IDelete, IPut } from '../authorization/actions';

import { ActionCreators as ActionCreatorsImportWaitlist, IWaitlistImportUploadResponse, IWaitlistImportProcessResponse } from './actions-waitlist-import';
import { ActionCreators as ActionCreatorsToolbox, IRecipientLabsReviewedResponse, IRecipientSaveToolboxAvoidsResponse, IToolboxAvoidsRequest, IEpletGraphDataResponse, IEpletGraphDataResult } from './actions-toolbox';
import { ActionCreators as ActionCreatorsPatientIntake, IPatientIntakeListResponse, IPatientIntakeListReplacementResponse, IPatientIntakeLookupDataResponse, IPatientIntakeResponse, IPassMicrositeRecipientProfile, IUpdateWorkflowDetailsRequest, IListRequest } from './actions-patient-intake';

import {
    ActionCreators as ActionCreatorsRecipientProfile, IAntibodyScreeningResponse, IAntibodyScreeningResultResponse, IRecipientActivityLogResponse, IRecipientContactInfoResponse,
    IRecipientFinancialInfoPostResponse, IRecipientFinancialInfoResponse, IRecipientGeneralDataResponse, IRecipientHeaderInfoResponse,
    IRecipientMedicalDetailsResponse, IRecipientPostTransplantResponse, IRecipientProfileHlaInfoResponse,
    IRecipientProfileRecordsPostResponse, IRecipientProfileRecordsResponse, IRecipientVoucherResponse, IRecipientXmResponse, IRecipientPairedDonorsListResponse,
    IPairedDonor,
    IRecipientDonorDeclinesResponse,
    IPostTransplantRejectionEpisodeResponse
} from './actions-recipient-profile';

export interface IPreselectListResponse {
    data: {
        success: boolean,
        message: string,
        results: IPreselectResult[]
    }
}

export interface IPreselectLookupData {
    data: {
        success: boolean,
        message: string,
        results: {
            hardBlockReasonList: IListItem[],
            donorPreselectReviewStatusList: IListItem[]
        }
    }
}

export interface IPotentialDonorListResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            recipient: IRecipient,
            fwtSummary: IFwtSummary,
            waitEstimate: string,
            recipientFwt: string,
            declinePercent: number,
            declineWait: string,
            potentialDonors: IPreselectDonorDetail[],
            pendingMatchHistoryDonorId: number,
            hardBlockReasonList: IListItem[],
            donorPreselectReviewStatusList: IListItem[]
        }
    }
}

export interface IEmpletMMDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donorAlias: string,
            recipientAlias: string,
            result: {
                recipientAlleles: IEmpletAllele[],
                donorAlleles: IEmpletAllele[],
                epletMismatches: IEpletMismatch[],
                errors: string[],
                singleEpletMmDr: number,
                singleEpletMmDq: number,
                singleEpletMmDqLow: number,
                totalEpletMmDr: number,
                totalEpletMmDrVerifiedAb: number,
                totalEpletMmDq: number,
                totalEpletMmDqVerifiedAb: number
            }
        }
    }
}
export interface IDonorFactSheetDataResponse {
    data: {
        success: boolean;
        results: {
            donorAlias: string;
            recipientAlias: string;
            donorId: number;
            recipientId: number;
            recipientCenterId: number;
            donorCenter: string;
            recipientCenter: string;
            abo: string;
            donorFirstName: string;
            donorLastName: string;
            address1: string;
            address2: string;
            city: string;
            state: string;
            postalCode: string;
            dateOfBirth: string; // ISO date string

            hlaA1Lr: number;
            hlaA2Lr: number;
            hlaB1Lr: number;
            hlaB2Lr: number;
            hlaDR1Lr: number;
            hlaDR2Lr: number;
            hlaBW1Lr: number;
            hlaBW2Lr: number;
            hlaCW1Lr: number;
            hlaCW2Lr: number;
            hlaDQ1Lr: number;
            hlaDQ2Lr: number;
            hlaDP1Lr: number;
            hlaDP2Lr: number;
            hlaDQA1Lr: number;
            hlaDQA2Lr: number;

            hlaA1Hr: string;
            hlaA2Hr: string;
            hlaB1Hr: string;
            hlaB2Hr: string;
            hlaDR1Hr: string;
            hlaDR2Hr: string;
            hlaBW1Hr: string | null;
            hlaBW2Hr: string | null;
            hlaCW1Hr: string;
            hlaCW2Hr: string;
            hlaDQ1Hr: string;
            hlaDQ2Hr: string;
            hlaDP1Hr: string;
            hlaDP2Hr: string;
            hlaDQA1Hr: string;
            hlaDQA2Hr: string;

            hlaDR51Lr: string;
            hlaDR52Lr: string;
            hlaDR53Lr: string;
            hlaDR51Hr: string;
            hlaDR52Hr: string;
            hlaDR53Hr: string;
        };
    };
}

export interface IRecipientShieldLookupResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            testTypes: IListItem[],
            defaultTestCycles: {
                testTypeId: number,
                months1: boolean,
                months3: boolean,
                months6: boolean,
                months12: boolean,
                months18: boolean,
                months24: boolean
            },
            stateList: IListItem[]
        }
    }
}

export interface IRecipientDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipient
    }
}

export interface IRecipientCenterDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipient
    }
}

export interface IEnrollmentRequest {
    recpFirstName: string,
    recpLastName: string,
    recpAddress: string,
    recpCity: string,
    recpZipCode: string,
    recpState: string,
    recpDob: string,
    recpRecentTransplantDate: string,
    recpMrn: string,
    recpIcdCode: string,
    parentChildTransplant: boolean,

    recpMedicareNumber: string,
    recpPrimaryInsurance: string,
    recpPolicyHolderName: string,
    recpGender: string,
    physFirstName: string,
    physLastName: string,
    physAddress: string,
    physCity: string,
    physZipCode: string,
    physState: string,
    physNpiNumber: string,

    orderTypes: IListItem[],
    months1: boolean,
    months3: boolean,
    months6: boolean,
    months12: boolean,
    months18: boolean,
    months24: boolean,
    orderTypeId: number
}

export interface IHighDeclineListResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            hardToMatchList: ITargetItem[],
            voucherList: ITargetItem[],
            unmatchedPairList: ITargetItem[],
            targetChainEndCutoff: number,
            unmatchedPairCutoff: number
        }
    }
}

export interface ITransplantedRecipientListResponse {
    data: {
        success: boolean,
        message: string,
        results: ITransplantedRecipient[]
    }
}

export interface ICenterRecipientListResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientForList[]
    }
}

export interface IAdminRecipientListResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipient[]
    }
}

export interface IAdminRecipientSaveResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipient
    }
}

export interface IRecipientLookupResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientSearchReportLookupData
    }
}

export interface IRecipientVoucherLookUpResponse {
    data: {
        success: boolean,
        results: IListItem[]
    }
}

export interface IRecipientReportLogResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientSearchLog
    }
}

export interface IRecipientRequest {
    centerId: number,
    show: number,
    status: number,
    alias: string,
    phiEmail: string,
    dob: string,
    // recipientId: number
}

export interface IRecipientMinDetailsResponse {
    data: {
        success: boolean,
        results: ICheekSwabRequest
    }
}

export interface IRecipientByCenter {
    id: number,
    alias: string,
    firstName: string,
    lastName: string,
    birthDate: string
}

export interface IRecipientListByCenterResponse {
    data: {
        success: boolean
        results: IRecipientByCenter[]
    }
}

export interface ICompatibilityResultsResponse {
    data: {
        success: boolean;
        results: {
            compatibilityResults: ICompatibilityResult[],
            donorMatchData: {
                totalHlaPoints: number,
                singleEpletMmRisk: ISingleEpletMmRisk,
                avoids: any[],
                compatibilityResult: ICompatibilityResult,
                lkdpiInfo: ILkdpiInfo,
                epletMmList: ISingleEpletMmRisk[]
            }
        };
    }
}

export interface IRecipientAliasDuplicateCheckResponse {
    data: {
        success: boolean
    }
}

// export interface IAddressValidationResponse {
//     data: {
//         success: boolean,
//         results: IUsPsAddressResponse
//     }
// }

//  

export interface IRecipientForSidebarResponse {
    data: {
        success: boolean,
        results: IRecipientForSidebar
    }
}

export interface IAdminUpdateRecipientRequest {
    currentCenterId: number,
    centerId: number,
    alias: string,
    isDeleted: boolean,
    isActive: boolean,
    patientTypeId: number,
    statusId: number,
    bloodType: string,
    sendComplianceEmails: boolean,
    mrn: string,
    firstName: string,
    lastName: string,
    isSwapSaver: boolean,
    isRealTimeSwapFailure: boolean,
    gender: string,
    birthDate: string,
    inactiveReason: number,
    chainId: number,
    clusterNumber: number,
    positionNumber: number,
    targetedChainEnd: boolean
}

export interface IRecipientSearchRequest {
    centerId: number,
    statusList: string[],
    typeList: string[],
    voucherType: string,
    alias: string,
    firstName: string,
    lastName: string,
    email: string,
    startDate?: string,
    endDate?: string
}

export const ActionCreatorsMain = {
    updateSurgeon: (recipientId: number, contactId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/surgeon?recipientId=${recipientId}&contactId=${contactId}`
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    get: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/${recipientId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getWaitlistReplacements: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICompatibilityResultsResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/waitlist-replacements`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getRecipientShieldLookup: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientShieldLookupResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient-shield/lookup`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getRecipientData: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientDataResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/${recipientId}/details`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    replaceWaitlistRecipient: (centerId: number, placeHolderRecipientId: number, selectedRecipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPut = {
            url: `${__API__}/center/${centerId}/recipient/${placeHolderRecipientId}/replace-with/${selectedRecipientId}`
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },
    addEnrollment: (centerId: number, recpId: number, request: IEnrollmentRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient-shield/enrollment/${recpId}`,
            data: request
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    parseWaitListFile: (waitListFile: File, centerId: number, deleteNotInFile: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();

        formData.append('waitListFile', waitListFile);

        var options: IPost = {
            url: `${__API__}/center/${centerId}/upload-waitlist?deleteNotInFile=${(deleteNotInFile ? 1 : 0)}`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        })
    },

    getPreselects: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IPreselectListResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/donor-pre-select?centerId=${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IPreselectListResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    getPreselectLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IPreselectLookupData> => {
        let options: IGet = {
            url: `${__API__}/recipient/donor-pre-select-lookup-data`
        };

        // return dispatch(AuthActionCreators.apiGet(options))
        //     .then((response: IPreselectLookupData) => {
        //         return Promise.resolve(response);
        //     })
        //     .catch((error: AxiosError) => {
        //         throw error;
        //     });

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getPotentialDonors: (recipientId: number, centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IPotentialDonorListResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/${recipientId}/potential-donors?centerId=${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IPotentialDonorListResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    getHighDeclines: () => async (dispatch: Dispatch<IApplicationState>): Promise<IHighDeclineListResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/high-declines`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    checkAlias: (recipientId: number, alias: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientAliasDuplicateCheckResponse> => {
        let options = {
            url: `${__API__}/recipient/${recipientId}/check-alias-availability`,
            data: {
                alias
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getList: (request: IRecipientSearchRequest, adminPage: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterRecipientListResponse> => {
        let statusList = request.statusList.join('&statuses=');
        let typeList = request.typeList.join('&types=');

        let options: IGet = {
            url: `${__API__}/recipient?centerId=${request.centerId}&alias=${request.alias}&firstName=${request.firstName}&lastName=${request.lastName}&email=${request.email}&statuses=${statusList}&types=${typeList}&voucherType=${request.voucherType}&adminPage=${adminPage}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getAdminRecipientList: (request: IRecipientRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IAdminRecipientListResponse> => {
        let options: IPost = {
            url: `${__API__}/admin/recipient`,
            data: request
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    doCenterSave: (recipient: IRecipient) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let { isActive, birthDate, gender } = recipient;

        let data = {
            isActive, birthDate, gender
        };

        var options = {
            url: `${__API__}/center/${recipient.centerId}/recipient/${recipient.id}/update-recipient`,
            data
        };

        return dispatch(AuthActionCreators.apiPut(options))
            .then((response: any) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    getTransplantedRecipients: (request: IRecipientSearchRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<ITransplantedRecipientListResponse> => {
        let typeList = request.typeList.join('&types=');

        let options: IGet = {
            url: `${__API__}/recipient/transplanted?centerId=${request.centerId}&alias=${request.alias}&firstName=${request.firstName}&lastName=${request.lastName}&email=${request.email}&types=${typeList}&voucherType=${request.voucherType}&startDate=${request.startDate}&endDate=${request.endDate}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    updateFromSidebar: (centerId: number, recipientId: number, alias: string, firstName: string, lastName: string, email: string, mrn: string, isActive: boolean, inactiveReasons: number, isDeleted: boolean, sendComplianceEmails: boolean, gender: string, birthDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/update-from-sidebar`,
            data: {
                alias,
                firstName,
                lastName,
                email,
                mrn,
                isActive,
                inactiveReasons,
                isDeleted,
                sendComplianceEmails,
                gender,
                birthDate
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getRecipientLookup: (centerId: number, isAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientLookupResponse> => {
        let url = isAdmin ? `${__API__}/admin/recipient/lookup-data?centerId=${centerId}` : `${__API__}/recipient/lookup-data?centerId=${centerId}`;

        let options: IGet = { url };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getRecipientVoucherLookup: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientVoucherLookUpResponse> => {
        var options: IPost = {
            url: `${__API__}/recipient/${recipientId}/voucher-look-up`
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    saveAdmin: (recipientId: number, request: IAdminUpdateRecipientRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IAdminRecipientSaveResponse> => {
        let {
            currentCenterId, centerId, isDeleted, statusId, sendComplianceEmails,
            patientTypeId, clusterNumber, chainId, positionNumber, alias, bloodType, isActive,
            firstName, lastName, mrn,
            isSwapSaver, isRealTimeSwapFailure, gender, birthDate, inactiveReason, targetedChainEnd
        } = request;

        var options = {
            url: `${__API__}/admin/recipient/${recipientId}/update`,
            data: {
                currentCenterId,
                centerId,
                isDeleted,
                statusId,
                sendComplianceEmails,
                patientTypeId,
                clusterNumber,
                chainId,
                positionNumber,
                alias,
                firstName, lastName,
                bloodType,
                isActive,
                inactiveReason,
                mrn,
                isSwapSaver,
                isRealTimeSwapFailure,
                gender,
                birthDate,
                targetedChainEnd
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    saveRecipientLogEntry: (id: number, recipientReportId: number, actionTakenId: number, comments: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientReportLogResponse> => {
        let options = {
            url: `${__API__}/admin/recipient/report-log-entry`,
            data: {
                id,
                recipientReportId,
                actionTakenId,
                comments
            }
        };

        if (id == 0) {
            return await dispatch(AuthActionCreators.apiPost(options));
        } else {
            return await dispatch(AuthActionCreators.apiPut(options));
        }
    },

    deleteRecipientLogEntry: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/recipient/report-log-entry/${id}`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },

    getRecipientMinById: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientMinDetailsResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/${donorId}/basic-info`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getEpletMmData: (recipientId: number, donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IEmpletMMDataResponse> => {
        let options: IGet = {
            url: `${__API__}/hla/eplet-mismatch-records?recipientId=${recipientId}&donorId=${donorId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDonorFactSheetData: (recipientId: number, donorId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorFactSheetDataResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/donor-fact-sheet/donor/${donorId}/recipient/${recipientId}?centerId=${centerId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    // getToolbox: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
    //     let options: IGet = {
    //         url: `${__API__}/recipient/${recipientId}/toolbox`
    //     };

    //     return await dispatch(AuthActionCreators.apiGet(options));
    // },
    downloadFile: (recipientId: number, donorId: number): any => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + `/hla/eplet-mismatch-pdf/${donorId}/${recipientId}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    getByCenter: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientListByCenterResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/by-center?centerId=${centerId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    requestReplaceWaitlistRecipientWithPair: (centerId: number, originalRecipientId: number, replacementRecipientId: number, bridgeDonorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/request-replacement-waitlist-patient-with-pair`,
            data: {
                originalRecipientId,
                replacementRecipientId,
                bridgeDonorId
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    validateAddress: (centerId: number, recipientId: number, address: string, address2: string, city: string, state: string, postalCode: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddressValidationResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/validate-address`,
            data: {
                address,
                address2,
                city,
                state,
                postalCode
            }
        }

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    getEmpletMMData: (recipientId: number, donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IEmpletMMDataResponse> => {
        let options: IGet = {
            url: `${__API__}/hla/eplet-mismatch-records?recipientId=${recipientId}&donorId=${donorId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getRecipientForSidebar: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientForSidebarResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/${recipientId}/side-bar`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    updateDiabeticStatus: (data: { recipientId: number, isDiabetic: boolean }[]) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/diabetic-status`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getRejectionEpisodes: (isAdmin: boolean, centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IPostTransplantRejectionEpisodeResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    saveRejectionEpisodeDraft: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection-draft`,
            data: rejection
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    createRejectionEpisode: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection`,
            data: rejection
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    updateRejectionEpisode: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPut = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection`,
            data: rejection
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    deleteRejectionEpisode: (centerId: number, recipientId: number, rejectionId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection/${rejectionId}/delete`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },

    unDeleteRejectionEpisode: (centerId: number, recipientId: number, rejectionId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPut = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/graft-rejection/${rejectionId}/undelete`
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    }
}

export interface IRecipientState {
}

var initialState: IRecipientState = {
};

export const ActionCreators = {
    ...ActionCreatorsMain,
    ...ActionCreatorsImportWaitlist,
    ...ActionCreatorsToolbox,
    ...ActionCreatorsPatientIntake,
    ...ActionCreatorsRecipientProfile
};

export {
    IWaitlistImportUploadResponse, IWaitlistImportProcessResponse,
    IRecipientLabsReviewedResponse, IRecipientSaveToolboxAvoidsResponse, IToolboxAvoidsRequest,
    IPatientIntakeListResponse, IPatientIntakeListReplacementResponse, IPatientIntakeResponse, IPatientIntakeLookupDataResponse,
    IPassMicrositeRecipientProfile, IUpdateWorkflowDetailsRequest,

    IRecipientHeaderInfoResponse,
    IRecipientDonorDeclinesResponse,
    IRecipientActivityLogResponse, IRecipientContactInfoResponse,
    IRecipientFinancialInfoPostResponse, IRecipientFinancialInfoResponse,
    IRecipientGeneralDataResponse,
    IRecipientMedicalDetailsResponse,
    IRecipientPostTransplantResponse,
    IRecipientProfileHlaInfoResponse,
    IRecipientProfileRecordsPostResponse, IRecipientProfileRecordsResponse,
    IRecipientVoucherResponse, IRecipientXmResponse, IAntibodyScreeningResponse, IAntibodyScreeningResultResponse,
    IRecipientPairedDonorsListResponse, IPairedDonor,

    IEpletGraphDataResponse, IEpletGraphDataResult,
    IListRequest
};

const createReducer = (initialState: IRecipientState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
};

export const Reducer = createReducer(initialState, handlers);
